/**
 * Customer Validator - Validation spécifique aux données customer
 */

/**
 * CustomerValidator: Classe responsable de la validation des données customer
 */
export class CustomerValidator {
  /**
   * validateCustomerData: Valide les données customer avec des vérifications complètes
   * @param customerData - Données customer à valider
   * @param t - Fonction de traduction
   * @return - Array des messages d'erreur de validation
   */
  static validate(customerData: any, t: any): string[] {
    const validationErrors: string[] = [];

    if (!customerData.name || typeof customerData.name !== "string" || customerData.name.trim().length === 0) {
      validationErrors.push(t("common:name"));
    }

    if (!customerData.activitySector) {
      validationErrors.push(t("common:activitySector"));
    }

    if (!customerData.nip || typeof customerData.nip !== "string" || customerData.nip.trim().length === 0) {
      validationErrors.push(t("common:nip"));
    }

    if (!customerData.address) {
      validationErrors.push(t("common:address"));
    }

    if (!customerData.languages || !Array.isArray(customerData.languages) || customerData.languages.length === 0) {
      validationErrors.push(t("common:languages"));
    }

    if (!customerData.contactInfo ||
      ((!customerData.contactInfo.email || typeof customerData.contactInfo.email !== "string" || customerData.contactInfo.email.trim().length === 0) &&
       (!customerData.contactInfo.emails || !Array.isArray(customerData.contactInfo.emails) || customerData.contactInfo.emails.length === 0))) {
      validationErrors.push(t("common:contactInfo") + " - " + t("common:email"));
    }

    if (!customerData.contactInfo ||
      !customerData.contactInfo.phone ||
      typeof customerData.contactInfo.phone !== "string" ||
      customerData.contactInfo.phone.trim().length === 0) {
      validationErrors.push(t("common:contactInfo") + " - " + t("common:phone"));
    }

    return validationErrors;
  }

  /**
   * validateField: Valide un champ spécifique des données customer
   * @param fieldName - Nom du champ à valider
   * @param fieldValue - Valeur du champ
   * @param t - Fonction de traduction
   * @return - Message d'erreur ou null si valide
   */
  static validateField(fieldName: string, fieldValue: any, t: any): string | null {
    switch (fieldName) {
    case "name":
      if (!fieldValue || typeof fieldValue !== "string" || fieldValue.trim().length === 0) {
        return t("common:name");
      }
      break;

    case "activitySector":
      if (!fieldValue) {
        return t("common:activitySector");
      }
      break;

    case "nip":
      if (!fieldValue || typeof fieldValue !== "string" || fieldValue.trim().length === 0) {
        return t("common:nip");
      }
      break;

    case "address":
      if (!fieldValue) {
        return t("common:address");
      }
      break;

    case "languages":
      if (!fieldValue || !Array.isArray(fieldValue) || fieldValue.length === 0) {
        return t("common:languages");
      }
      break;

    case "email":
      if (!fieldValue || (typeof fieldValue !== "string" && !Array.isArray(fieldValue)) || (typeof fieldValue === "string" && fieldValue.trim().length === 0) || (Array.isArray(fieldValue) && fieldValue.length === 0)) {
        return t("common:contactInfo") + " - " + t("common:email");
      }
      break;

    case "phone":
      if (!fieldValue || typeof fieldValue !== "string" || fieldValue.trim().length === 0) {
        return t("common:contactInfo") + " - " + t("common:phone");
      }
      break;
    }

    return null; // Champ valide
  }
}

/**
 * Interface for the validation strategies (for future extension)
 */
export interface ValidationStrategy {
  validate(data: any, t: any): string[];
}

/**
 * Implementation of the validation strategy for customers
 */
export class CustomerValidationStrategy implements ValidationStrategy {
  validate(customerData: any, t: any): string[] {
    return CustomerValidator.validate(customerData, t);
  }
}

/**
 * Factory to create validators according to the entity
 * Example of usage to extend to other entities :
 *
 * // To add Technician :
 * // 1. Create technician-validator.ts
 * // 2. Add the case in ValidatorFactory
 * // case "technician":
 * //   return new TechnicianValidationStrategy();
 */
export class ValidatorFactory {
  static createValidator(entityName: string): ValidationStrategy | null {
    switch (entityName) {
    case "customer":
      return new CustomerValidationStrategy();

      // Example to add other entities :
      // case "technician":
      //   return new TechnicianValidationStrategy();
      // case "workorder":
      //   return new WorkorderValidationStrategy();

    default:
      return null;
    }
  }
}
